/**
 * Pantalla «Módulos por organización» (`/pages/org-modules`, header › Administration › Modules): una herramienta de
 * PLATAFORMA (súper admin) con la que se activan y desactivan los módulos del catálogo `public.modules` de CUALQUIER
 * organización habilitada. Lo que exige de la sesión de Sirius y lo que la UI cuenta de cada módulo.
 *
 * Solo la usan las organizaciones que tienen activo el módulo `modules-admin` (`MODULES_ADMIN_MODULE_KEY`, en el JWT
 * como `orgModules[].key`; se activa la primera vez con `apps/api/db/manual/enable_modules_admin.sql`) y, dentro de
 * ellas, los usuarios con el access `manage_org_modules` (`MANAGE_ORG_MODULES_ACCESS`, en el JWT como `access[]`;
 * `super_admin` lo tiene implícitamente). Las dos condiciones las comprueban la entrada del menú
 * (`HeaderUserComponent.canManageOrgModules`) y el guard de la ruta (`OrgModulesAdminGuard`). La autoridad es la API
 * (`/org-modules`): exige `manage_org_modules` en el token y comprueba en BD, en cada petición, que la organización
 * actual tiene `modules-admin` activo y que el usuario tiene el grant en ella; si no, responde 403
 * `MODULES_ADMIN_NOT_ENABLED_API_MESSAGE` en todas las rutas. No hay alcance por membresía: un tenant sin
 * `modules-admin` no administra ni sus propios módulos.
 */
export const MANAGE_ORG_MODULES_ACCESS = 'manage_org_modules';

/** Módulo de catálogo que convierte a una organización en administradora de los módulos de todas las demás. */
export const MODULES_ADMIN_MODULE_KEY = 'modules-admin';

/** 403 de la API cuando la organización actual no tiene `modules-admin` activo o el usuario no tiene el grant en ella. */
export const MODULES_ADMIN_NOT_ENABLED_API_MESSAGE =
  'Module administration is not enabled for your organization';

/** 409 de la API al desactivar `modules-admin` de la organización actual del token (anti-bloqueo). */
export const MODULES_ADMIN_SELF_DISABLE_API_MESSAGE =
  'You cannot disable module administration for your own organization';

/** Lo que la UI explica de un módulo del catálogo (textos en español; la clave y el nombre vigentes vienen de la API). */
export interface OrgModuleCatalogInfo {
  /** Nombre de respaldo mientras no se ha cargado el catálogo de la API (`modules.name` manda cuando está). */
  nombre: string;
  /** Qué es el módulo, en una frase. */
  descripcion: string;
  /** Pantallas, menús o capacidades que aparecen al activarlo. */
  habilita: string[];
  /** Otros módulos (por `key`) que deben estar activos para que funcione de verdad. */
  requiere?: string[];
}

/**
 * Descripciones por `modules.key`. El catálogo es de solo lectura en la UI: una clave que no esté aquí se muestra
 * igualmente (solo nombre y clave), así que añadir un módulo nuevo al catálogo no exige tocar este archivo.
 */
export const ORG_MODULE_CATALOG_INFO: Record<string, OrgModuleCatalogInfo> = {
  'aetna-affiliates': {
    nombre: 'Aetna Affiliates',
    descripcion: 'Afiliados Aetna: datos de afiliación en la ficha de contacto y su reporte.',
    habilita: ['Panel «Aetna Affiliate» en contactos', 'Reporte Aetna'],
  },
  'service-module': {
    nombre: 'Service Module',
    descripcion: 'Servicios contratables: catálogo de servicios, contratos y suscripciones.',
    habilita: ['Servicios', 'Contratos y sus ítems'],
  },
  'billing-module': {
    nombre: 'Billing Module',
    descripcion: 'Facturas, contactos de facturación, catálogo y dashboard de facturación',
    habilita: [
      'Dashboard de cobranza',
      'Facturas',
      'Contactos de facturación',
      'Catálogo de productos',
      'BackOffice e-CF',
    ],
  },
  'fiscal-profiles-module': {
    nombre: 'Perfiles Fiscales',
    descripcion:
      'Perfiles fiscales: identidad fiscal del emisor y de los contactos (RNC/NCF) y formulario de factura.',
    habilita: [
      'Perfil fiscal en Configuración del negocio',
      'Datos fiscales en contactos y facturas',
    ],
  },
  'ecf-module': {
    nombre: 'Facturación Electrónica (e-CF)',
    descripcion:
      'Facturación electrónica (e-CF) ante la DGII: certificado, secuencias, emisión y certificación.',
    habilita: [
      'Certificado e-CF',
      'Secuencias e-CF',
      'Emisión y firma de e-CF',
      'Certificación DGII',
    ],
    requiere: ['fiscal-profiles-module', 'billing-module'],
  },
  'agents-engine': {
    nombre: 'Agentes IA (agents-engine)',
    descripcion: 'Agentes IA; asigna los accesos agents_engine_* en Roles',
    habilita: ['Menú «Agentes IA»: agentes, tools, modelos, runs, aprobaciones y playground'],
  },
  'education-module': {
    nombre: 'Educación',
    descripcion: 'Educación: contactos (padres) e inscripciones (alumnos).',
    habilita: ['Contactos (padres)', 'Inscripciones (alumnos)'],
  },
  'investment-projects': {
    nombre: 'Proyectos de inversión',
    descripcion: 'Proyectos de inversión: seguimiento de proyectos y sus inversores.',
    habilita: ['Menú «Projects»'],
  },
  [MODULES_ADMIN_MODULE_KEY]: {
    nombre: 'Administrador de módulos',
    descripcion: 'Administración de los módulos de todas las organizaciones (plataforma)',
    habilita: ['Menú Administration › Modules'],
  },
};
